/**
 * Server functions for Customer Account API OAuth 2.0 PKCE flow.
 *
 * These run exclusively on the server and handle:
 * - Login initiation (generate PKCE, set HTTP-only cookies, return auth URL)
 * - OAuth callback (exchange code for tokens, set token cookies)
 * - Token refresh
 *
 * Logout is handled client-side (see AuthActionsProvider) because Nitro's dev
 * proxy rejects large cookie headers with `invalid transfer-encoding`.
 * The id_token is returned to the client and stored in localStorage.
 */
import { createServerFn } from "@tanstack/react-start";
import {
  deleteCookie,
  getCookie,
  setCookie,
} from "@tanstack/react-start/server";
import {
  OAUTH_CODE_VERIFIER_KEY,
  OAUTH_NONCE_KEY,
  OAUTH_STATE_KEY,
  USER_ACCESS_TOKEN_KEY,
  USER_REFRESH_TOKEN_KEY,
} from "../../../consts/keys";
import {
  buildAuthorizeUrl,
  discoverAuthEndpoints,
  exchangeCodeForTokens,
  generateCodeChallenge,
  generateCodeVerifier,
  generateNonce,
  generateState,
  getNonce,
  refreshAccessToken as refreshToken,
} from "./customer-account";

// ─── Cookie options ────────────────────────────────────────────────────────────

/** Short-lived cookies for OAuth flow state (10 min). */
const OAUTH_FLOW_COOKIE_OPTIONS = {
  httpOnly: true,
  secure: true,
  sameSite: "lax" as const,
  path: "/",
  maxAge: 600, // 10 minutes
};

/** Long-lived cookies for tokens. */
function tokenCookieOptions(maxAge: number) {
  return {
    httpOnly: true,
    secure: true,
    sameSite: "lax" as const,
    path: "/",
    maxAge,
  };
}

// ─── Initiate Login ────────────────────────────────────────────────────────────

/**
 * Server function: Generate PKCE values, store them in HTTP-only cookies,
 * and return the Shopify authorization URL.
 */
export const initiateLogin = createServerFn({ method: "POST" })
  .inputValidator((data: { locale?: string }) => data)
  .handler(async ({ data }) => {
    const authConfig = await discoverAuthEndpoints();

    // Generate PKCE values
    const codeVerifier = await generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();
    const nonce = generateNonce();

    // Store PKCE state in HTTP-only cookies (server-side only)
    setCookie(OAUTH_CODE_VERIFIER_KEY, codeVerifier, OAUTH_FLOW_COOKIE_OPTIONS);
    setCookie(OAUTH_STATE_KEY, state, OAUTH_FLOW_COOKIE_OPTIONS);
    setCookie(OAUTH_NONCE_KEY, nonce, OAUTH_FLOW_COOKIE_OPTIONS);

    // Build the authorization URL
    const authorizeUrl = buildAuthorizeUrl({
      authorizationEndpoint: authConfig.authorization_endpoint,
      codeChallenge,
      state,
      nonce,
      locale: data.locale,
    });

    return { authorizeUrl };
  });

// ─── Handle OAuth Callback ─────────────────────────────────────────────────────

/**
 * Server function: Exchange the authorization code for tokens.
 * Validates state, exchanges code using PKCE verifier, sets token cookies.
 */
export const handleOAuthCallback = createServerFn({ method: "POST" })
  .inputValidator((data: { code: string; state: string }) => data)
  .handler(async ({ data }) => {
    const { code, state } = data;

    // ── Validate state ───────────────────────────────────────────────────────

    const storedState = getCookie(OAUTH_STATE_KEY);
    if (!storedState || storedState !== state) {
      throw new Error("OAuth state mismatch — possible CSRF attack.");
    }

    // ── Retrieve PKCE verifier ───────────────────────────────────────────────

    const codeVerifier = getCookie(OAUTH_CODE_VERIFIER_KEY);
    if (!codeVerifier) {
      throw new Error(
        "Missing code verifier — OAuth flow may have expired or been tampered with."
      );
    }

    const storedNonce = getCookie(OAUTH_NONCE_KEY);

    // ── Exchange code for tokens ─────────────────────────────────────────────

    const authConfig = await discoverAuthEndpoints();
    const tokens = await exchangeCodeForTokens(
      authConfig.token_endpoint,
      code,
      codeVerifier
    );

    // ── Validate nonce (if we stored one) ────────────────────────────────────

    if (storedNonce && tokens.id_token) {
      const returnedNonce = getNonce(tokens.id_token);
      if (returnedNonce !== storedNonce) {
        throw new Error("Nonce mismatch — possible replay attack.");
      }
    }

    // ── Store tokens in cookies ────────────────────────────────────────────

    // Access token: NOT httpOnly so the client can read it for auth state
    // (it's short-lived, refresh token stays httpOnly)
    setCookie(USER_ACCESS_TOKEN_KEY, tokens.access_token, {
      secure: true,
      sameSite: "lax" as const,
      path: "/",
      maxAge: tokens.expires_in,
    });

    if (tokens.refresh_token) {
      // Refresh tokens last much longer (30 days)
      setCookie(
        USER_REFRESH_TOKEN_KEY,
        tokens.refresh_token,
        tokenCookieOptions(60 * 60 * 24 * 30)
      );
    }

    // ── Clean up OAuth flow cookies ──────────────────────────────────────────

    deleteCookie(OAUTH_CODE_VERIFIER_KEY);
    deleteCookie(OAUTH_STATE_KEY);
    deleteCookie(OAUTH_NONCE_KEY);

    // Return id_token so the client can store it in localStorage for SSO logout
    return { success: true, idToken: tokens.id_token };
  });

// ─── Refresh Token ─────────────────────────────────────────────────────────────

/**
 * Server function: Refresh the access token using the stored refresh token.
 */
export const refreshAccessTokenFn = createServerFn({
  method: "POST",
}).handler(async () => {
  const currentRefreshToken = getCookie(USER_REFRESH_TOKEN_KEY);

  if (!currentRefreshToken) {
    throw new Error("No refresh token available.");
  }

  const authConfig = await discoverAuthEndpoints();
  const tokens = await refreshToken(
    authConfig.token_endpoint,
    currentRefreshToken
  );

  // Update cookies with new tokens (access token is NOT httpOnly for client reads)
  setCookie(USER_ACCESS_TOKEN_KEY, tokens.access_token, {
    secure: true,
    sameSite: "lax" as const,
    path: "/",
    maxAge: tokens.expires_in,
  });

  if (tokens.refresh_token) {
    setCookie(
      USER_REFRESH_TOKEN_KEY,
      tokens.refresh_token,
      tokenCookieOptions(60 * 60 * 24 * 30)
    );
  }

  return { success: true, expiresIn: tokens.expires_in };
});

// ─── Get Access Token (for use in server functions) ────────────────────────────

/**
 * Server function: Read the current access token from cookies.
 * Returns null if not authenticated.
 */
export const getAccessTokenFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const accessToken = getCookie(USER_ACCESS_TOKEN_KEY);
    return accessToken ?? null;
  }
);
